(function () {
  const MAIN_HIDDEN_CLASS = "product__media-item--variant-hidden";
  const THUMB_HIDDEN_CLASS = "product__media-subitem--variant-hidden";

  function parseVariantIds(value) {
    if (!value) return [];
    return value
      .split(",")
      .map((id) => Number(id))
      .filter(Boolean);
  }

  function getMediaIdFromDataset(datasetValue) {
    if (!datasetValue) return null;
    return datasetValue.split("-").pop();
  }

  function handleize(value) {
    return (value || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/['"]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  function getSelectedAltGroup(container, variant) {
    if (container?.dataset?.altMediaGrouping !== "true") return null;
    const optionPosition = Number(container.dataset.groupOptionPosition);
    if (!optionPosition || !variant?.options?.length) return null;
    return handleize(variant.options[optionPosition - 1]);
  }

  function matchesAltGroup(item, selectedGroup) {
    if (!selectedGroup) return true;
    const itemGroup = item.dataset.mediaGroup || "";
    if (!itemGroup) return true;
    return itemGroup === selectedGroup;
  }

  function shouldShowMedia({
    mode,
    item,
    variantId,
    featuredMediaId,
    selectedGroup,
  }) {
    const mediaId = getMediaIdFromDataset(
      item.dataset.mediaId || item.dataset.mediaSubId
    );
    const attachedToVariant = item.dataset.attachedToVariant === "true";
    const variantIds = parseVariantIds(item.dataset.variantIds);

    let show = true;

    if (mode === "show_only_variant_media") {
      show = Boolean(featuredMediaId && mediaId === String(featuredMediaId));
    } else if (mode === "hide_unselected_variant_media") {
      if (!attachedToVariant) {
        show = true;
      } else if (featuredMediaId && mediaId === String(featuredMediaId)) {
        show = true;
      } else {
        show = variantIds.includes(variantId);
      }
    }

    return show && matchesAltGroup(item, selectedGroup);
  }

  function getMediaContainer(sectionId) {
    return document.querySelector(
      `[data-section="product-media-${sectionId}"]`
    );
  }

  function syncSwiperSlideClass(item, show) {
    if (!item.parentElement?.classList.contains("swiper-wrapper")) return;
    // Keep swiper-slide only on visible items so Swiper ignores hidden media
    // without destroying the instance (destroy/recreate breaks thumbs layout).
    item.classList.toggle("swiper-slide", show);
  }

  function setItemVisibility(item, show, hiddenClass) {
    item.classList.toggle(hiddenClass, !show);
    syncSwiperSlideClass(item, show);
  }

  function refreshSwiper(swiper) {
    if (!swiper || swiper.destroyed) return;
    swiper.updateSize();
    swiper.updateSlides();
    swiper.updateProgress();
    swiper.updateSlidesClasses();
    swiper.update();
  }

  function updateThumbsSlidesPerView(thumbsEl) {
    if (!thumbsEl?.swiper || thumbsEl.swiper.destroyed) return;

    const visibleCount = thumbsEl.querySelectorAll(
      `.swiper-slide:not(.${THUMB_HIDDEN_CLASS})`
    ).length;
    const safeCount = Math.max(visibleCount, 1);
    const swiper = thumbsEl.swiper;

    if (typeof swiper.params.slidesPerView === "number") {
      swiper.params.slidesPerView = Math.min(
        swiper.params.slidesPerView,
        safeCount
      );
    }

    if (swiper.params.breakpoints) {
      Object.keys(swiper.params.breakpoints).forEach((bp) => {
        const bpParams = swiper.params.breakpoints[bp];
        if (bpParams && typeof bpParams.slidesPerView === "number") {
          bpParams.slidesPerView = Math.min(bpParams.slidesPerView, safeCount);
        }
      });
    }
  }

  function refreshThumbsHeight(container) {
    const sliderEl = container.querySelector(".js-media-list");
    const thumbsEl = container.querySelector(".js-media-sublist");
    if (!sliderEl || !thumbsEl) return;
    const height = sliderEl.getBoundingClientRect().height;
    if (height > 0) {
      thumbsEl.style.height = `${height - 1}px`;
    }
  }

  function updateSwipers(container, featuredMediaId) {
    const sliderEl = container.querySelector(".js-media-list");
    const thumbsEl = container.querySelector(".js-media-sublist");

    // Preserve the live direction. Recreating / forcing breakpoint direction was
    // flipping bottom (horizontal) thumbs into a side (vertical) column.
    const thumbsWasVertical = Boolean(thumbsEl?.swiper?.isVertical?.());
    const mainWasVertical = Boolean(sliderEl?.swiper?.isVertical?.());

    updateThumbsSlidesPerView(thumbsEl);
    refreshSwiper(thumbsEl?.swiper);
    refreshSwiper(sliderEl?.swiper);

    if (thumbsEl?.swiper && !thumbsEl.swiper.destroyed) {
      if (thumbsWasVertical && thumbsEl.swiper.isHorizontal()) {
        thumbsEl.swiper.changeDirection("vertical", true);
      } else if (!thumbsWasVertical && thumbsEl.swiper.isVertical()) {
        thumbsEl.swiper.changeDirection("horizontal", true);
      }
    }

    if (sliderEl?.swiper && !sliderEl.swiper.destroyed) {
      if (mainWasVertical && sliderEl.swiper.isHorizontal()) {
        sliderEl.swiper.changeDirection("vertical", true);
      } else if (!mainWasVertical && sliderEl.swiper.isVertical()) {
        sliderEl.swiper.changeDirection("horizontal", true);
      }
    }

    refreshThumbsHeight(container);
    slideToVisibleMedia(container, featuredMediaId);
  }

  function slideToVisibleMedia(container, featuredMediaId) {
    const sliderEl = container.querySelector(".js-media-list");
    if (!sliderEl?.swiper || sliderEl.swiper.destroyed) return;

    const slides = Array.from(sliderEl.swiper.slides || []);
    if (!slides.length) return;

    let targetIndex = 0;

    // Honor the "show second media first" option on the first run only: land on
    // the first visible slide (the reordered second media) instead of the
    // variant's featured media. The flag is consumed once so that later variant
    // changes keep sliding to the selected variant's media as usual.
    const forceFirstVisibleMedia =
      container.dataset.secondMediaFirst === "true" &&
      container.dataset.secondMediaFirstPending === "true";

    if (forceFirstVisibleMedia) {
      container.dataset.secondMediaFirstPending = "false";
      const firstVisibleIndex = slides.findIndex(
        (slide) => !slide.classList.contains(MAIN_HIDDEN_CLASS)
      );
      targetIndex = firstVisibleIndex === -1 ? 0 : firstVisibleIndex;
    } else if (featuredMediaId) {
      const featuredIndex = slides.findIndex((slide) => {
        const mediaId = getMediaIdFromDataset(slide.dataset?.mediaId);
        return mediaId === String(featuredMediaId);
      });
      if (featuredIndex !== -1) targetIndex = featuredIndex;
    }

    sliderEl.swiper.slideTo(targetIndex, 0);
    refreshSwiper(sliderEl.swiper);

    const thumbsEl = container.querySelector(".js-media-sublist");
    if (thumbsEl?.swiper && !thumbsEl.swiper.destroyed) {
      thumbsEl.swiper.slideTo(targetIndex, 0);
      refreshSwiper(thumbsEl.swiper);
    }
  }

  function updateThumbsWrapperVisibility(container) {
    const thumbsWrapper = container.querySelector(
      ".product__media-sublist-outer"
    );
    if (!thumbsWrapper) return;

    const visibleThumbs = container.querySelectorAll(
      `.product__media-subitem[data-media-sub-id]:not(.${THUMB_HIDDEN_CLASS})`
    );
    thumbsWrapper.classList.toggle("hidden", visibleThumbs.length <= 1);
  }

  function updateVariantMediaDisplay(sectionId, variant) {
    const container = getMediaContainer(sectionId);
    if (!container) return;

    const mode = container.dataset.variantMediaDisplay || "show_all";
    const hasAltGrouping = container.dataset.altMediaGrouping === "true";

    // Avoid touching Swiper when filtering is inactive.
    if (mode === "show_all" && !hasAltGrouping) return;

    const variantId = variant?.id;
    const featuredMediaId = variant?.featured_media?.id;
    const selectedGroup = getSelectedAltGroup(container, variant);

    const mainItems = container.querySelectorAll(
      ".product__media-item[data-media-id]"
    );
    const thumbItems = container.querySelectorAll(
      ".product__media-subitem[data-media-sub-id]"
    );

    if (mode === "show_all" && !selectedGroup) {
      mainItems.forEach((item) =>
        setItemVisibility(item, true, MAIN_HIDDEN_CLASS)
      );
      thumbItems.forEach((item) =>
        setItemVisibility(item, true, THUMB_HIDDEN_CLASS)
      );
      updateThumbsWrapperVisibility(container);
      updateSwipers(container, featuredMediaId);
      return;
    }

    mainItems.forEach((item) => {
      const show = shouldShowMedia({
        mode,
        item,
        variantId,
        featuredMediaId,
        selectedGroup,
      });
      setItemVisibility(item, show, MAIN_HIDDEN_CLASS);
    });

    thumbItems.forEach((item) => {
      const show = shouldShowMedia({
        mode,
        item,
        variantId,
        featuredMediaId,
        selectedGroup,
      });
      setItemVisibility(item, show, THUMB_HIDDEN_CLASS);
    });

    updateThumbsWrapperVisibility(container);
    updateSwipers(container, featuredMediaId);
  }

  function initVariantMediaGrouping(container) {
    const mode = container.dataset.variantMediaDisplay;
    const hasAltGrouping = container.dataset.altMediaGrouping === "true";
    if ((!mode || mode === "show_all") && !hasAltGrouping) return;

    const sectionId = container.dataset.section?.replace("product-media-", "");
    if (!sectionId) return;

    const variantPicker = document.querySelector(
      `#variant-picker-${sectionId}`
    );
    const selectedVariantScript = variantPicker?.querySelector(
      "[data-selected-variant]"
    );
    if (!selectedVariantScript) return;

    try {
      const variant = JSON.parse(selectedVariantScript.textContent);
      updateVariantMediaDisplay(sectionId, variant);
    } catch (error) {
      console.error(error);
    }
  }

  function initAllVariantMediaGrouping() {
    document
      .querySelectorAll(
        "[data-variant-media-display], [data-alt-media-grouping='true']"
      )
      .forEach(initVariantMediaGrouping);
  }

  window.updateVariantMediaDisplay = updateVariantMediaDisplay;

  document.addEventListener("DOMContentLoaded", initAllVariantMediaGrouping);
  document.addEventListener(
    "shopify:section:load",
    initAllVariantMediaGrouping
  );
})();
